/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import { Component, Input } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { L10nService } from '@vmw/ngx-vip';
import { isEmpty, isObject } from 'underscore';

import {
    AVI_REPEATED_STRINGS_RANGE_VALIDATOR_KEY,
    AVI_REPEATED_STRINGS_UNIQUENESS_VALIDATOR_KEY,
    AVI_SLIDER_RANGE_VALIDATOR_KEY,
    CONFIG_FIELD_INPUT_VALIDATOR_KEY,
    CRON_EXPRESSION_VALIDATOR_KEY,
    PORT_VALIDATOR_KEY,
    REGEX_PATTERN_VALIDATOR_KEY,
    UNIQUE_VALUE_INPUT_VALIDATOR_KEY,
} from 'ng/modules/avi-forms/validators';

import {
    IP_RANGE_VALIDATOR_KEY,
} from 'ng/modules/avi-forms/validators/in-subnet/in-subnet-validation.validator';

import {
    STRING_LENGTH_FIELD_VALIDATOR_KEY,
} from 'ng/modules/avi-forms/directives/string-length-input-validation';

import {
    WAF_ADD_EXCEPTION_DIALOG_FORM_VALIDATOR_KEY,
} from 'ng/modules/waf/validators/waf-add-exception-dialog-form';

import * as l10n from './avi-input-container-error.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const errorsMap = new Map([
    [REGEX_PATTERN_VALIDATOR_KEY, l10nKeys.regexPatternError],
    [AVI_REPEATED_STRINGS_RANGE_VALIDATOR_KEY, l10nKeys.rangeError],
    [AVI_REPEATED_STRINGS_UNIQUENESS_VALIDATOR_KEY, l10nKeys.uniquenessError],
    ['required', l10nKeys.requiredError],
    [CONFIG_FIELD_INPUT_VALIDATOR_KEY, l10nKeys.rangeError],
    [CRON_EXPRESSION_VALIDATOR_KEY, l10nKeys.cronExpressionValidationError],
    [PORT_VALIDATOR_KEY, l10nKeys.portValidateError],
    [AVI_SLIDER_RANGE_VALIDATOR_KEY, l10nKeys.rangeError],
    [IP_RANGE_VALIDATOR_KEY, l10nKeys.ipRangeValidateError],
    [UNIQUE_VALUE_INPUT_VALIDATOR_KEY, l10nKeys.uniquenessError],
    [STRING_LENGTH_FIELD_VALIDATOR_KEY, l10nKeys.maxStringLengthValidateError],
    [WAF_ADD_EXCEPTION_DIALOG_FORM_VALIDATOR_KEY, l10nKeys.atLeastOneValueValidateError],
]);

/**
 * @description Component to display validation errors in the AviInputContainer component.
 * @author alextsg
 */
@Component({
    selector: 'avi-input-container-error',
    template: '<div>{{ errorMessage }}</div>',
    standalone: true,
})
export class AviInputContainerErrorComponent {
    /**
     * Object of validationErrors
     */
    @Input()
    public validationErrors: ValidationErrors;

    /**
     * Optional Error text. If present, overrides all other errors.
     */
    @Input()
    public customError = '';

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Returns an error to be displayed. If customError exists, return that. If validationErrors
     * exists, return the error from the first error key.
     */
    public get errorMessage(): string {
        if (this.customError) {
            return this.customError;
        }

        if (!isEmpty(this.validationErrors)) {
            const key = Object.keys(this.validationErrors)[0];
            const value = this.validationErrors[key];

            if (isObject(value) && 'errorMessage' in value) {
                return value.errorMessage;
            } else if (Array.isArray(value)) {
                return this.l10nService.getMessage(errorsMap.get(key), value);
            }

            return this.l10nService.getMessage(errorsMap.get(key));
        }

        return '';
    }
}
